<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif; background: #f4f6f9; min-height: 100vh;">
    <div class="max-w-7xl mx-auto px-4 py-8 space-y-6">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-2">
        <h1 class="text-3xl font-black text-slate-800 uppercase tracking-tight">Prefeituras</h1>
        <p class="text-sm text-slate-500 font-medium mt-1">Gerenciamento de municípios, CNPJs e unidades centrais</p>
      </div>

      <!-- LISTAGEM -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <!-- BARRA DE FERRAMENTAS -->
          <div style="background-color: #333333; display: flex; align-items: center; padding: 10px 20px; width: 100%; box-sizing: border-box;">

              <!-- TÍTULO -->
              <div style="display: flex; align-items: center; gap: 8px; flex-shrink: 0;">
                <v-icon color="orange" small>mdi-city</v-icon>
                <span style="color: white; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em;">MUNICIPALIDADES CADASTRADAS</span>
              </div>

              <!-- AÇÕES FIXADAS À DIREITA -->
              <div style="margin-left: auto; display: flex; align-items: center; gap: 12px; flex-shrink: 0;">
                
                <!-- BUSCA RÁPIDA -->
                <div style="width: 250px; flex-shrink: 0;">
                  <v-text-field
                    v-model="search"
                    placeholder="PESQUISAR..."
                    outlined dense hide-details
                    class="field-input-dark"
                    prepend-inner-icon="mdi-magnify"
                  />
                </div>

                <!-- BOTÃO NOVO -->
                <button
                  @click="$router.push('/prefectures-manager/create')"
                  style="height: 32px; width: auto; white-space: nowrap; flex-shrink: 0; background-color: #f37021; color: white; display: inline-flex; align-items: center; gap: 6px; border-radius: 8px; padding: 0 14px; font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; border: none; cursor: pointer; transition: background-color 0.2s;"
                  onmouseover="this.style.backgroundColor='#e0601a'"
                  onmouseout="this.style.backgroundColor='#f37021'"
                >
                  <v-icon color="white" x-small>mdi-plus</v-icon>
                  NOVO
                </button>

              </div>
          </div>

          <div class="overflow-x-auto">
              <v-data-table
                :headers="headers"
                :items="data"
                :search="search"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                @page-count="pageCount = $event"
                :loading="loader"
                loading-text="CARREGANDO PREFEITURAS..."
                class="custom-table"
                hide-default-footer
              >
                <!-- NOME / RAZÃO SOCIAL -->
                <template v-slot:item.prefecture_social_name="{ item }">
                  <div class="flex flex-col py-2">
                    <span class="text-sm font-bold text-slate-700 uppercase">{{ item.prefecture_social_name }}</span>
                    <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest">MUNICÍPIO / PREFEITURA</span>
                  </div>
                </template>

                <!-- CNPJ -->
                <template v-slot:item.prefecture_cnpj="{ item }">
                  <span class="text-[11px] font-bold text-slate-500">{{ item.prefecture_cnpj }}</span>
                </template>

                <!-- CONTATO / CELULAR -->
                <template v-slot:item.prefecture_cel="{ item }">
                  <span class="text-[11px] font-bold text-slate-500">{{ item.prefecture_cel || '-' }}</span>
                </template>

                <!-- AÇÕES -->
                <template v-slot:item.action="{ item }">
                  <div class="flex justify-end gap-2 px-4">
                    <button 
                      @click="$router.push(`/prefectures-manager/secretariats/${item.prefecture_id}`)"
                      class="p-2 hover:bg-slate-50 text-slate-500 rounded-lg transition-colors"
                      title="Gerenciar Secretarias"
                    >
                      <v-icon small color="slate-400">mdi-office-building-cog-outline</v-icon>
                    </button>
                    <button 
                      @click="$router.push(`/prefectures-manager/edit/${item.prefecture_id}`)"
                      class="p-2 hover:bg-orange-50 text-orange-600 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <v-icon small color="orange">mdi-pencil</v-icon>
                    </button>
                    <button 
                      @click="deleteItem(item.prefecture_id)"
                      class="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                      title="Excluir"
                    >
                      <v-icon small color="red">mdi-delete</v-icon>
                    </button>
                  </div>
                </template>

                <template v-slot:no-data>
                  <div class="py-12 flex flex-col items-center justify-center text-slate-400">
                    <v-icon size="48" color="slate-100">mdi-city-variant-outline</v-icon>
                    <p class="mt-2 text-[10px] font-bold uppercase tracking-widest">Nenhuma prefeitura encontrada</p>
                  </div>
                </template>
              </v-data-table>

              <!-- Custom Pagination Footer -->
              <div class="bg-slate-50 px-6 py-6 border-t border-slate-100 flex flex-col items-center justify-center gap-4 text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                  <span>Exibindo {{ (page - 1) * itemsPerPage + 1 }} de {{ data.length }} registros</span>
                  
                  <div class="flex gap-2">
                      <button 
                        @click="page > 1 ? page-- : null" 
                        :disabled="page === 1"
                        class="px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 transition-all"
                      >
                        Anterior
                      </button>
                      
                      <button 
                        v-for="p in visiblePages" 
                        :key="p"
                        @click="page = p"
                        :class="p === page ? 'bg-slate-700 text-white shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                        class="w-8 h-8 border rounded-lg transition-all flex items-center justify-center"
                      >
                        {{ p }}
                      </button>

                      <button 
                        @click="page < pageCount ? page++ : null" 
                        :disabled="page === pageCount || pageCount === 0"
                        class="px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 transition-all"
                      >
                        Próximo
                      </button>
                  </div>
              </div>
          </div>
      </section>

      <!-- Snackbar -->
      <v-snackbar v-model="snack.show" :color="snack.color" timeout="4000" top elevation="24" class="custom-snack">
        <div class="flex items-center gap-3">
          <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
          <span class="text-xs font-bold uppercase tracking-widest">{{ snack.message }}</span>
        </div>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      search: "",
      loader: true,
      data: [],
      snack: {
        show: false,
        message: "",
        color: ""
      },
      headers: [
        { text: "RAZÃO SOCIAL / MUNICÍPIO", value: "prefecture_social_name" },
        { text: "CNPJ", value: "prefecture_cnpj" },
        { text: "CONTATO", value: "prefecture_cel" },
        { text: "AÇÕES", align: "right", value: "action", sortable: false }
      ],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  computed: {
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);
      if (end - start + 1 < totalVisible) start = Math.max(1, end - totalVisible + 1);
      for (let i = start; i <= end; i++) if (i > 0) pages.push(i);
      return pages;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", true);
      this.$http.get("/prefectures").then(r => {
        this.data = r.data;
        this.loader = false;
        this.$store.dispatch("setLoader", false);
      }).catch(e => {
        this.loader = false;
        this.snack.show = true;
        this.snack.message = e.message || e;
        this.snack.color = "error";
        this.$store.dispatch("setLoader", false);
      });
    },
    deleteItem(id) {
      if (confirm("Deseja realmente excluir esta prefeitura? Esta ação não pode ser desfeita.")) {
        this.$store.dispatch("setLoader", true);
        this.$http.delete(`/prefectures/${id}`).then(r => {
          this.snack.show = true; this.snack.message = r.data.result || "Prefeitura excluída!"; this.snack.color = "success";
          this.initialize();
        }).catch(e => {
          this.snack.show = true; this.snack.message = e.message; this.snack.color = "error";
          this.$store.dispatch("setLoader", false);
        });
      }
    }
  }
};
</script>

<style scoped>
.field-input-dark >>> .v-input__control .v-input__slot {
  background-color: #333333 !important;
  border-radius: 6px !important;
}

.field-input-dark >>> .v-input__control .v-input__slot fieldset {
  border-color: #444444 !important;
}

.field-input-dark >>> input {
  color: #ffffff !important;
  font-weight: 700 !important;
  font-size: 11px !important;
  text-transform: uppercase !important;
}

.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep th { 
  color: #94a3b8 !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 10px !important; 
  letter-spacing: 0.05em !important;
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 13px !important;
}

.custom-snack >>> .v-snack__wrapper {
  border-radius: 10px !important;
}
</style>